import {
  DiscountMethod,
  CustomerEligibilityV2Type,
  UiConfigDiscountType,
  AmountType,
  PrerequisiteType,
  CouponType,
  DiscountPrerequisite,
  DiscountEntitlement,
  PricePrerequisite,
  QuantityPrerequisite,
  BxgyEntitlement,
  BundleEntitlement,
  AmountEntitlement,
  ShippingEntitlement,
  BundlePrerequisite,
  StandardPrerequisite,
  PurchaseConditionAmountType,
  DiscountStackMode,
  DiscountFormV2StateType
} from 'src/lib/types/discountsV2'
import { format } from 'date-fns'

export const isPricePrerequisite = (prereq: DiscountPrerequisite): prereq is PricePrerequisite =>
  (prereq as StandardPrerequisite).prerequisiteType === PrerequisiteType.PRICE

export const isQuantityPrerequisite = (prereq: DiscountPrerequisite): prereq is QuantityPrerequisite =>
  (prereq as StandardPrerequisite).prerequisiteType === PrerequisiteType.QUANTITY

export const isBundlePrerequisite = (prereq: DiscountPrerequisite): prereq is BundlePrerequisite =>
  prereq.type === CouponType.BUNDLE

export const isBxgyEntitlement = (entitlement: DiscountEntitlement): entitlement is BxgyEntitlement =>
  entitlement.type === CouponType.BXGY

export const isBundleEntitlement = (entitlement: DiscountEntitlement): entitlement is BundleEntitlement =>
  entitlement.type === CouponType.BUNDLE

export const isAmountEntitlement = (entitlement: DiscountEntitlement): entitlement is AmountEntitlement =>
  entitlement.type === CouponType.AMOUNT

export const isShippingEntitlement = (entitlement: DiscountEntitlement): entitlement is ShippingEntitlement =>
  entitlement.type === CouponType.SHIPPING

type TFunction = (key: string, params?: Record<string, any>) => string

export const getMethodTitle = (state: DiscountFormV2StateType, t: TFunction) => {
  const { applicationMethod } = state.entitlement
  if (applicationMethod.method === DiscountMethod.AUTOMATIC) {
    return applicationMethod.title || t('discounts_v2.automatic')
  }
  return applicationMethod.title || applicationMethod.discountCode || t('discounts_v2.method_code_placeholder')
}

export const getEntitlementSummary = (state: DiscountFormV2StateType, t: TFunction): string => {
  const { entitlement, uiConfig } = state

  if (
    uiConfig.type === UiConfigDiscountType.AMOUNT_OFF ||
    uiConfig.type === UiConfigDiscountType.AMOUNT_OFF_PRODUCTS ||
    uiConfig.type === UiConfigDiscountType.AMOUNT_OFF_UPTO ||
    uiConfig.type === UiConfigDiscountType.EXIT_COUPON
  ) {
    if (isAmountEntitlement(entitlement)) {
      const amount = entitlement.concessionAmount ?? 0
      const typeSymbol = entitlement.amountType === AmountType.PERCENTAGE ? '%' : '₹'

      if (uiConfig.type === UiConfigDiscountType.AMOUNT_OFF_PRODUCTS) {
        const catalogueGroups = entitlement.entitledCatalogue || []
        const metaDataGroups = entitlement.metaData?.entitledCatalogue || []
        const groupSummaries = catalogueGroups.map((group, index) => {
          const metaItems = metaDataGroups[index] || []
          const itemNames =
            metaItems.length > 0
              ? `${metaItems.length} ${t('discounts_v2.common_specific_items')}`
              : t('discounts_v2.common_specific_items')
          if (group.quantity && group.quantity > 1) {
            return t('discounts_v2.summary_buy_qty_of_names', { qty: group.quantity, names: itemNames }).replace(
              'Buy ',
              ''
            )
          }
          return itemNames
        })
        const names = groupSummaries.join(' and ') || t('discounts_v2.common_specific_items')
        return t('discounts_v2.summary_off_on_names', {
          amount: `${entitlement.amountType === AmountType.FLAT ? typeSymbol : ''}${amount}${
            entitlement.amountType === AmountType.PERCENTAGE ? typeSymbol : ''
          }`,
          names
        })
      }

      if (uiConfig.type === UiConfigDiscountType.AMOUNT_OFF_UPTO) {
        const maxAmount = entitlement.maximumConcessionAmount
        return t('discounts_v2.summary_off_upto_on_order', {
          amount: `${amount}%`,
          max: maxAmount ? `₹${maxAmount}` : ''
        }).replace(' upto  on order', ' on order')
      }

      if (uiConfig.type === UiConfigDiscountType.EXIT_COUPON) {
        const maxAmount = entitlement.maximumConcessionAmount
        const amountStr = `${entitlement.amountType === AmountType.FLAT ? typeSymbol : ''}${amount}${
          entitlement.amountType === AmountType.PERCENTAGE ? typeSymbol : ''
        }`
        if (entitlement.amountType === AmountType.PERCENTAGE && maxAmount) {
          return t('discounts_v2.summary_off_upto_on_order', { amount: amountStr, max: `₹${maxAmount}` })
        }
        return t('discounts_v2.summary_off_on_entire_order', { amount: amountStr })
      }

      return t('discounts_v2.summary_off_on_entire_order', {
        amount: `${entitlement.amountType === AmountType.FLAT ? typeSymbol : ''}${amount}${
          entitlement.amountType === AmountType.PERCENTAGE ? typeSymbol : ''
        }`
      })
    }
  }

  if (uiConfig.type === UiConfigDiscountType.BXGY || uiConfig.type === UiConfigDiscountType.BXGY_AUTO_ADDED) {
    if (isBxgyEntitlement(entitlement) || isAmountEntitlement(entitlement)) {
      const catalogueGroups = entitlement.entitledCatalogue || []
      const metaDataGroups = entitlement.metaData?.entitledCatalogue || []
      const groupSummaries = catalogueGroups.map((group, index) => {
        const metaItems = metaDataGroups[index] || []
        const itemNames =
          metaItems.length > 0
            ? `${metaItems.length} ${t('discounts_v2.common_specific_items')}`
            : t('discounts_v2.common_specific_items')
        if (group.quantity && group.quantity > 0) {
          return t('discounts_v2.summary_buy_qty_of_names', { qty: group.quantity, names: itemNames }).replace(
            'Buy ',
            ''
          )
        }
        return itemNames
      })
      const names = groupSummaries.join(' and ') || t('discounts_v2.common_specific_items')
      if (entitlement.amountType === AmountType.PERCENTAGE && entitlement.concessionAmount === 100) {
        return t('discounts_v2.summary_get_names_for_free', { names })
      }
      const typeSymbol = entitlement.amountType === AmountType.PERCENTAGE ? '%' : '₹'
      const amount = entitlement.concessionAmount
      return t('discounts_v2.summary_get_names_for_off', {
        names,
        amount: `${entitlement.amountType === AmountType.FLAT ? typeSymbol : ''}${amount}${
          entitlement.amountType === AmountType.PERCENTAGE ? typeSymbol : ''
        }`
      })
    }
  }

  if (uiConfig.type === UiConfigDiscountType.BUNDLE) {
    if (isBundleEntitlement(entitlement)) {
      const amount = entitlement.concessionAmount ?? 0
      const groups = entitlement.metaData?.entitledCatalogue || []
      const count = groups.flat().length
      const names =
        count > 0 ? `${count} ${t('discounts_v2.common_bundle_items')}` : t('discounts_v2.common_bundle_items')
      if (entitlement.amountType === AmountType.FLAT) {
        return t('discounts_v2.summary_get_names_for_amount', { names, amount: `₹${amount}` })
      }
      return t('discounts_v2.summary_off_on_names', { amount: `${amount}%`, names })
    }
  }

  if (uiConfig.type === UiConfigDiscountType.SHIPPING && isShippingEntitlement(entitlement)) {
    if (entitlement.amountType === AmountType.PERCENTAGE && entitlement.concessionAmount === 100) {
      return t('discounts_v2.summary_free_shipping')
    }
    const typeSymbol = entitlement.amountType === AmountType.PERCENTAGE ? '%' : '₹'
    const amount = entitlement.concessionAmount
    return t('discounts_v2.summary_off_shipping', {
      amount: `${entitlement.amountType === AmountType.FLAT ? typeSymbol : ''}${amount}${
        entitlement.amountType === AmountType.PERCENTAGE ? typeSymbol : ''
      }`
    })
  }

  if (uiConfig.type === UiConfigDiscountType.FREEBIE || uiConfig.type === UiConfigDiscountType.FREEBIE_SELECTOR) {
    if (isBxgyEntitlement(entitlement) || isAmountEntitlement(entitlement)) {
      const qty = 'entitledProductCount' in entitlement ? entitlement.entitledProductCount : 1
      const groups = entitlement.metaData?.entitledCatalogue || []
      const count = groups.flat().length
      const names = count > 0 ? `${count} ${t('discounts_v2.common_products')}` : t('discounts_v2.common_products')
      return t('discounts_v2.summary_get_qty_of_names_for_free', { qty, names })
    }
  }

  return '--'
}

export const getPrerequisiteSummary = (state: DiscountFormV2StateType, t: TFunction): string => {
  const { prerequisite, uiConfig } = state

  if (isBundlePrerequisite(prerequisite)) {
    return t('discounts_v2.summary_buy_bundle')
  }

  if (
    uiConfig.type === UiConfigDiscountType.BXGY ||
    uiConfig.type === UiConfigDiscountType.BXGY_AUTO_ADDED ||
    uiConfig.type === UiConfigDiscountType.FREEBIE ||
    uiConfig.type === UiConfigDiscountType.FREEBIE_SELECTOR
  ) {
    if (isQuantityPrerequisite(prerequisite)) {
      const groups = prerequisite.metaData?.prerequisiteCatalogue || []
      const firstGroup = groups[0] || []
      const isCollection =
        (prerequisite as StandardPrerequisite).purchaseConditionAmountType ===
        PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
      const suffix = isCollection
        ? t('discounts_v2.common_specific_collections')
        : t('discounts_v2.common_specific_products')
      const names = firstGroup.length > 0 ? `${firstGroup.length} ${suffix}` : suffix
      const catGroup = prerequisite.prerequisiteCatalogue[0]
      if (catGroup && catGroup.type === PrerequisiteType.QUANTITY) {
        if (catGroup.maxQuantity) {
          return t('discounts_v2.summary_buy_qty_range_of_names', {
            qty: catGroup.quantity,
            min: catGroup.quantity,
            max: catGroup.maxQuantity,
            names
          })
        }
        return t('discounts_v2.summary_buy_qty_of_names', { qty: catGroup.quantity, names })
      }
      return `Buy ${names}`
    }
    if (isPricePrerequisite(prerequisite)) {
      const groups = prerequisite.metaData?.prerequisiteCatalogue || []
      const firstGroup = groups[0] || []
      const isCollection =
        (prerequisite as StandardPrerequisite).purchaseConditionAmountType ===
        PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
      const suffix = isCollection
        ? t('discounts_v2.common_specific_collections')
        : t('discounts_v2.common_specific_products')
      const names = firstGroup.length > 0 ? `${firstGroup.length} ${suffix}` : suffix
      const catGroup = prerequisite.prerequisiteCatalogue[0]
      if (catGroup && catGroup.type === PrerequisiteType.PRICE) {
        if (catGroup.maxPrice) {
          return t('discounts_v2.summary_buy_worth_range_of_names', {
            min: catGroup.price,
            max: catGroup.maxPrice,
            names
          })
        }
        return t('discounts_v2.summary_buy_worth_of_names', { amount: catGroup.price, names })
      }
      return `Buy worth ${names}`
    }
    const groups = (prerequisite as StandardPrerequisite).metaData?.prerequisiteCatalogue || []
    const isCollection =
      (prerequisite as StandardPrerequisite).purchaseConditionAmountType ===
      PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
    const suffix = isCollection
      ? t('discounts_v2.common_specific_collections')
      : t('discounts_v2.common_specific_products')
    const names = groups.flat().length > 0 ? `${groups.flat().length} ${suffix}` : suffix
    return `Buy ${names}`
  }

  if (isPricePrerequisite(prerequisite) || isQuantityPrerequisite(prerequisite)) {
    if (
      prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_PRODUCTS ||
      prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
    ) {
      const catalogueGroups = prerequisite.prerequisiteCatalogue || []
      const metaDataGroups = prerequisite.metaData?.prerequisiteCatalogue || []
      const isCollection =
        prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
      const suffix = isCollection
        ? t('discounts_v2.common_specific_collections')
        : t('discounts_v2.common_specific_products')
      const groupSummaries = catalogueGroups.map((group, index) => {
        const metaItems = metaDataGroups[index] || []
        const itemNames = metaItems.length > 0 ? `${metaItems.length} ${suffix}` : suffix
        if (group.type === PrerequisiteType.PRICE) {
          if (group.maxPrice) {
            return t('discounts_v2.summary_buy_worth_range_of_names', {
              min: group.price,
              max: group.maxPrice,
              names: itemNames
            }).replace('Buy worth ', '')
          }
          return t('discounts_v2.summary_buy_worth_of_names', { amount: group.price, names: itemNames }).replace(
            'Buy worth ',
            ''
          )
        }
        if (group.type === PrerequisiteType.QUANTITY) {
          if (group.maxQuantity) {
            return t('discounts_v2.summary_buy_qty_range_of_names', {
              min: group.quantity,
              max: group.maxQuantity,
              names: itemNames
            }).replace('Buy ', '')
          }
          return t('discounts_v2.summary_buy_qty_of_names', { qty: group.quantity, names: itemNames }).replace(
            'Buy ',
            ''
          )
        }
        return itemNames
      })
      if (groupSummaries.length > 0) {
        return `Buy ${groupSummaries.join(' and ')}`
      }
      return t('discounts_v2.summary_buy_worth_of_names', {
        amount: '',
        names: t('discounts_v2.common_specific_items')
      }).replace(' worth  of', '')
    }

    if (isPricePrerequisite(prerequisite)) {
      const minPrice = prerequisite.prerequisiteSubtotalRange ?? 0
      const maxPrice = prerequisite.prerequisiteMaxOrderValue
      if (maxPrice) {
        return t('discounts_v2.summary_orders_between', { min: minPrice, max: maxPrice })
      }
      return t('discounts_v2.summary_orders_above', { min: minPrice })
    }
    if (isQuantityPrerequisite(prerequisite)) {
      const minQty = prerequisite.prerequisiteCountToEntitlement ?? 0
      const maxQty = prerequisite.prerequisiteMaxCountForEntitlement
      if (maxQty) {
        return t('discounts_v2.summary_orders_with_qty_items', { min: minQty, max: maxQty })
      }
      return t('discounts_v2.summary_orders_with_min_items', { min: minQty })
    }
  }

  return t('discounts_v2.no_minimum_requirements')
}

export const getEligibilitySummary = (state: DiscountFormV2StateType, t: TFunction): string => {
  const { customerEligibility } = state.prerequisite
  switch (customerEligibility.type) {
    case CustomerEligibilityV2Type.ALL_CUSTOMERS:
      return t('discounts_v2.summary_eligible_all')
    case CustomerEligibilityV2Type.FIRST_TIME_CUSTOMERS:
      return t('discounts_v2.summary_eligible_first_time')
    case CustomerEligibilityV2Type.LIFETIME_ORDER_COUNT:
      return t('discounts_v2.summary_eligible_lifetime')
    case CustomerEligibilityV2Type.SPECIFIC_CUSTOMERS:
      return t('discounts_v2.summary_eligible_specific')
    case CustomerEligibilityV2Type.CUSTOMER_SPECIFIC:
      return t('discounts_v2.summary_eligible_segments')
    case CustomerEligibilityV2Type.UTM_PARAMS:
      return t('discounts_v2.summary_eligible_utms')
    default:
      return t('discounts_v2.summary_eligible_all')
  }
}

export const getUsageSummary = (state: DiscountFormV2StateType, t: TFunction): string => {
  const { usageLimit } = state.prerequisite
  if (!usageLimit.maxApplicableCount && !usageLimit.maxApplicablePerUserCount) {
    return t('discounts_v2.summary_unlimited_usage')
  }
  const parts = []
  if (usageLimit.maxApplicableCount)
    parts.push(t('discounts_v2.summary_limited_total_uses', { count: usageLimit.maxApplicableCount }))
  if (usageLimit.maxApplicablePerUserCount)
    parts.push(t('discounts_v2.summary_limited_per_customer', { count: usageLimit.maxApplicablePerUserCount }))
  return parts.join(', ')
}

export const getVisibilitySummary = (state: DiscountFormV2StateType, t: TFunction): string => {
  const { availability } = state.prerequisite
  return availability.discoverable
    ? t('discounts_v2.summary_visible_everyone')
    : t('discounts_v2.summary_hidden_everyone')
}

export const getAvailabilitySummary = (state: DiscountFormV2StateType, t: TFunction): string => {
  const { availability } = state.prerequisite
  const startDate = availability.startDate ? format(new Date(availability.startDate), 'd MMM, yyyy') : '--'
  const endDate = availability.endDate
    ? t('discounts_v2.summary_active_till', { date: format(new Date(availability.endDate), 'd MMM, yyyy') })
    : ''
  return t('discounts_v2.summary_active_from', { date: startDate }) + endDate
}

export const getChannelsSummary = (state: DiscountFormV2StateType, t: TFunction): string => {
  const { availability } = state.prerequisite
  const channelCount = availability.enabledChannelIds.length
  if (channelCount === 0) return t('discounts_v2.summary_no_channels')
  const channelsLabel = channelCount > 1 ? t('discounts_v2.channel_plural') : t('discounts_v2.channel_singular')
  return t('discounts_v2.summary_available_on_channels', { count: channelCount, channels: channelsLabel })
}

export const getCombinationSummary = (state: DiscountFormV2StateType, t: TFunction): string => {
  switch (state.prerequisite.stackability?.stackMode) {
    case DiscountStackMode.ALL:
      return t('discounts_v2.summary_combinable_all')
    case DiscountStackMode.NONE:
      return t('discounts_v2.summary_not_combinable')
    case DiscountStackMode.CUSTOM:
      return t('discounts_v2.summary_combinable_specific')
    default:
      return t('discounts_v2.summary_combinable_all')
  }
}

export const getSummaryItems = (state: DiscountFormV2StateType, t: TFunction): string[] => [
  getEntitlementSummary(state, t),
  getPrerequisiteSummary(state, t),
  getEligibilitySummary(state, t),
  getUsageSummary(state, t),
  getVisibilitySummary(state, t),
  getCombinationSummary(state, t),
  getAvailabilitySummary(state, t),
  getChannelsSummary(state, t)
]
